import type { TipoSolicitacao } from './solicitacao.types';

/**
 * E-mails que o SI manda ao Cliente pedindo (entrada) ou encerrando (saída) acesso/máquina
 * de quem vai atuar lá — ex.: SWAP. Configurados por Cliente em Configurações → E-mails para
 * o Cliente (si_config_emails_cliente) e abertos no Outlook já preenchidos pela Solicitação
 * (mailto — sai da caixa de quem envia, ver PrepararEmailClienteModal).
 */
export type MomentoEmailCliente = 'entrada' | 'saida';

export const MOMENTO_EMAIL_CLIENTE_LABELS: Record<MomentoEmailCliente, string> = {
  entrada: 'Entrada no cliente',
  saida: 'Saída do cliente',
};

export interface ModeloEmailCliente {
  id: string;
  clienteId: string;
  clienteNome: string;
  momento: MomentoEmailCliente;
  /** Em quais tipos de Solicitação o e-mail aparece. */
  tiposSolicitacao: TipoSolicitacao[];
  para: string[];
  cc: string[];
  assunto: string;
  corpo: string;
}

export type ModeloEmailClienteSalvar = Omit<ModeloEmailCliente, 'id'> & { id?: string };

/**
 * `digital`: o sistema já sabe (cadastro, pré-cadastro, alocação, Movimentação).
 * `abertura`: só o Cliente/quem pede sabe — pedido no bloco "Dados para o cliente" da abertura
 * da Solicitação (Solicitacao.dadosCliente), só quando algum modelo do Cliente usa a variável.
 */
export type OrigemVariavelEmailCliente = 'digital' | 'abertura';

export interface VariavelEmailCliente {
  chave: string;
  label: string;
  origem: OrigemVariavelEmailCliente;
  /** Dado do Digital que, quando não está cadastrado (ver verificarDadosEmailCliente), é
   * pedido na abertura — hoje só o RG. */
  aberturaSeFaltar?: boolean;
  /** Texto de ajuda do campo na abertura. */
  ajuda?: string;
}

export const VARIAVEIS_EMAIL_CLIENTE: readonly VariavelEmailCliente[] = [
  { chave: 'saudacao', label: 'Bom dia/Boa tarde', origem: 'digital' },
  { chave: 'nome', label: 'Nome completo', origem: 'digital' },
  { chave: 'funcao', label: 'Função', origem: 'digital' },
  { chave: 'email_raro', label: 'E-mail Raro', origem: 'digital' },
  { chave: 'email_pessoal', label: 'E-mail pessoal', origem: 'digital' },
  { chave: 'telefone', label: 'Telefone', origem: 'digital' },
  { chave: 'cpf', label: 'CPF', origem: 'digital' },
  {
    chave: 'rg',
    label: 'RG',
    origem: 'digital',
    aberturaSeFaltar: true,
    ajuda: 'O RG desta pessoa não está cadastrado no Digital.',
  },
  { chave: 'data_nascimento', label: 'Data de nascimento', origem: 'digital' },
  { chave: 'endereco', label: 'Endereço', origem: 'digital' },
  { chave: 'so', label: 'Sistema operacional', origem: 'digital' },
  { chave: 'cliente', label: 'Cliente', origem: 'digital' },
  { chave: 'data_inicio', label: 'Data de início', origem: 'digital' },
  { chave: 'data_termino', label: 'Data de término', origem: 'digital' },
  // Sempre pedido na abertura (decisão do usuário): nem toda alocação tem Responsável Raro,
  // e em Admissão ainda não existe alocação.
  {
    chave: 'gestor_raro',
    label: 'Gestor Raro',
    origem: 'abertura',
    ajuda: 'Vem do Responsável Raro da alocação, quando preenchido.',
  },
  {
    chave: 'gestor_cliente',
    label: 'Gestor no cliente',
    origem: 'abertura',
    ajuda: 'Vem do "Responsável do Cliente" da alocação, quando preenchido.',
  },
  { chave: 'contrato_cliente', label: 'Contrato com o cliente', origem: 'abertura' },
  { chave: 'projeto_cliente', label: 'Projeto/equipe no cliente', origem: 'abertura' },
];

/** O que quem abre informou no bloco "Dados para o cliente" — só variáveis de `abertura`. */
export type DadosClienteSolicitacao = Record<string, string>;

export function variaveisUsadas(textos: string[]): Set<string> {
  const usadas = new Set<string>();
  textos.forEach((texto) => {
    texto.replace(/\{([a-z_]+)\}/g, (original, chave: string) => {
      usadas.add(chave);
      return original;
    });
  });
  return usadas;
}

/** Variáveis de `abertura` usadas pelos modelos, na ordem do catálogo — mais as do Digital
 * que faltam no cadastro (`faltando`, ver `aberturaSeFaltar`). */
export function camposAberturaDosModelos(
  modelos: ModeloEmailCliente[],
  faltando: ReadonlySet<string> = new Set()
): VariavelEmailCliente[] {
  const usadas = variaveisUsadas(modelos.flatMap((m) => [m.assunto, m.corpo]));
  return VARIAVEIS_EMAIL_CLIENTE.filter(
    (v) => usadas.has(v.chave) && (v.origem === 'abertura' || (v.aberturaSeFaltar && faltando.has(v.chave)))
  );
}

/** Modelos que valem pra um tipo de Solicitação em algum dos Clientes envolvidos (o da
 * Solicitação e os das Movimentações — Mudança de Projeto sai de um e entra em outro). */
export function modelosAplicaveis(
  modelos: ModeloEmailCliente[],
  tipo: TipoSolicitacao | '',
  clienteIds: Array<string | null | undefined>
): ModeloEmailCliente[] {
  if (!tipo) return [];
  return modelos.filter((m) => m.tiposSolicitacao.includes(tipo) && clienteIds.includes(m.clienteId));
}

/** Troca cada {variavel} pelo valor; sem valor fica vazio (o rótulo do modelo continua lá
 * pra quem envia ver o que faltou). */
export function preencherEmailCliente(texto: string, valores: Record<string, string | null | undefined>): string {
  return texto.replace(/\{([a-z_]+)\}/g, (original, chave: string) =>
    chave in valores ? (valores[chave] ?? '').trim() : original
  );
}

/** Ponto de partida ao criar um modelo — os textos que o SI já mandava pra SWAP. */
export const MODELO_EMAIL_CLIENTE_SUGERIDO: Record<MomentoEmailCliente, Pick<ModeloEmailCliente, 'assunto' | 'corpo' | 'tiposSolicitacao'>> = {
  entrada: {
    tiposSolicitacao: ['admissao', 'mudanca_projeto', 'nova_demanda'],
    assunto: 'Solicitação de usuário e máquina - {nome}',
    corpo: [
      'Olá, {saudacao}! Tudo bem?',
      '',
      'Dando continuidade à solicitação de usuários para atuação nos projetos de modernização, encaminhamos mais uma solicitação prioritária para criação de usuário e disponibilização de máquina.',
      '',
      'Segue abaixo os dados do colaborador:',
      '',
      'Nome Completo: {nome}',
      'Função: {funcao}',
      'E-mail Raro: {email_raro}',
      'E-mail pessoal: {email_pessoal}',
      'Telefone: {telefone}',
      'CPF: {cpf}',
      'RG: {rg}',
      'Data de nascimento: {data_nascimento}',
      'Endereço: {endereco}',
      'SO: {so}',
      'Tipo de contrato: {contrato_cliente}',
      'Nome do projeto ou equipe que vai atuar: {projeto_cliente}',
      'Nome do Gestor {cliente}: {gestor_cliente}',
      'Nome do Gestor Raro: {gestor_raro}',
      'Data de início: {data_inicio}',
      '',
      'Caso necessitem de alguma informação adicional, estamos à disposição.',
      'Obrigado!',
    ].join('\n'),
  },
  saida: {
    tiposSolicitacao: ['desligamento', 'mudanca_projeto', 'devolucao'],
    assunto: 'Saída do projeto {cliente} - {nome}',
    corpo: [
      'Olá, {saudacao}! Tudo bem?',
      '',
      'Para conhecimento e providências, informo que o colaborador abaixo não fará mais parte do projeto {cliente}.',
      '',
      'Nome Completo: {nome}',
      'E-mail Raro: {email_raro}',
      'E-mail pessoal: {email_pessoal}',
      'CPF: {cpf}',
      'Nome do Gestor: {gestor_cliente}',
      'Data de término: {data_termino}',
      '',
      'O equipamento será recolhido e posteriormente será direcionado a outro colaborador.',
      '',
      'Caso necessitem de alguma informação adicional, estamos à disposição.',
      'Obrigado!',
    ].join('\n'),
  },
};

/** {saudacao} — pela hora em que o e-mail é preparado ("Olá, bom dia!"). */
export function saudacaoPelaHora(agora: Date = new Date()): string {
  const hora = agora.getHours();
  if (hora < 12) return 'bom dia';
  if (hora < 18) return 'boa tarde';
  return 'boa noite';
}

/** "Nome <email>, outro@x" → lista de e-mails. Aceita vírgula, ponto e vírgula ou quebra de linha. */
export function listaEmails(texto: string): string[] {
  return texto
    .split(/[,;\n]/)
    .map((parte) => (parte.match(/<([^>]+)>/)?.[1] ?? parte).trim())
    .filter(Boolean);
}

export function emailValido(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/** mailto com para/cópia/assunto/corpo — abre o Outlook (ou o cliente de e-mail padrão). */
export function montarLinkEmail(para: string[], cc: string[], assunto: string, corpo: string): string {
  const parametros = [
    cc.length ? `cc=${cc.join(',')}` : null,
    `subject=${encodeURIComponent(assunto)}`,
    `body=${encodeURIComponent(corpo.replace(/\n/g, '\r\n'))}`,
  ].filter(Boolean);
  return `mailto:${para.join(',')}?${parametros.join('&')}`;
}
